/// Diagram of a Tropical Revolving Storm with dangerous and
/// navigable semicircles marked. Hemisphere toggle changes the
/// rotation + zone labelling: Northern hemisphere TRS rotate
/// anticlockwise, dangerous side is on the right of the track;
/// Southern hemisphere TRS rotate clockwise, dangerous side on the
/// left.

interface Props {
  hemisphere?: "N" | "S";
  size?: number;
}

const VW = 360;
const VH = 360;
const STROKE = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const DANGER = "#c0382b";
const NAV = "#1e6091";
const TRACK = "#7c3aed";

export function TrsZonesFigure({ hemisphere = "N", size = VW }: Props) {
  const isN = hemisphere === "N";

  const centre = { x: VW / 2, y: VH / 2 };
  const R = 110;
  // Track direction: storm moves UP the chart (north). Dangerous side
  // is on the right for N hem, left for S hem.
  const dangerSide: "L" | "R" = isN ? "R" : "L";

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`TRS — ${hemisphere}orthern hemisphere`}
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      <rect
        x={0}
        y={0}
        width={VW}
        height={VH}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
      />

      {/* Half-circle fills for dangerous + navigable zones. */}
      <path
        d={
          dangerSide === "R"
            ? `M ${centre.x} ${centre.y - R} A ${R} ${R} 0 0 1 ${centre.x} ${centre.y + R} Z`
            : `M ${centre.x} ${centre.y - R} A ${R} ${R} 0 0 0 ${centre.x} ${centre.y + R} Z`
        }
        fill={`${DANGER}25`}
      />
      <path
        d={
          dangerSide === "R"
            ? `M ${centre.x} ${centre.y - R} A ${R} ${R} 0 0 0 ${centre.x} ${centre.y + R} Z`
            : `M ${centre.x} ${centre.y - R} A ${R} ${R} 0 0 1 ${centre.x} ${centre.y + R} Z`
        }
        fill={`${NAV}25`}
      />

      {/* Storm circle outline + centre. */}
      <circle
        cx={centre.x}
        cy={centre.y}
        r={R}
        fill="none"
        stroke={STROKE}
        strokeWidth={1.5}
      />
      <circle cx={centre.x} cy={centre.y} r={5} fill={DANGER} />
      <text
        x={centre.x}
        y={centre.y + 18}
        fontSize={11}
        fontWeight={700}
        fill={DANGER}
        textAnchor="middle"
      >
        L
      </text>

      {/* Track arrow — moves north (up) in this diagram. */}
      <line
        x1={centre.x}
        y1={VH - 30}
        x2={centre.x}
        y2={30}
        stroke={TRACK}
        strokeWidth={2.5}
        strokeDasharray="6 4"
      />
      <polygon
        points={`${centre.x - 6},${40} ${centre.x + 6},${40} ${centre.x},${28}`}
        fill={TRACK}
      />
      <text
        x={centre.x + 10}
        y={40}
        fontSize={11}
        fontWeight={700}
        fill={TRACK}
      >
        Storm track
      </text>

      {/* Wind rotation arrows — anticlockwise for N, clockwise for S. */}
      <ArrowAround centre={centre} R={R - 18} anticlockwise={isN} />

      {/* Zone labels. */}
      <text
        x={dangerSide === "R" ? centre.x + 70 : centre.x - 70}
        y={centre.y - R - 4}
        fontSize={12}
        fontWeight={700}
        fill={DANGER}
        textAnchor="middle"
      >
        DANGEROUS
      </text>
      <text
        x={dangerSide === "R" ? centre.x + 70 : centre.x - 70}
        y={centre.y - R + 12}
        fontSize={10}
        fill={DANGER}
        textAnchor="middle"
      >
        semicircle
      </text>

      <text
        x={dangerSide === "R" ? centre.x - 70 : centre.x + 70}
        y={centre.y - R - 4}
        fontSize={12}
        fontWeight={700}
        fill={NAV}
        textAnchor="middle"
      >
        NAVIGABLE
      </text>
      <text
        x={dangerSide === "R" ? centre.x - 70 : centre.x + 70}
        y={centre.y - R + 12}
        fontSize={10}
        fill={NAV}
        textAnchor="middle"
      >
        semicircle
      </text>

      {/* Escape strategy arrows — point boats AWAY from the storm. */}
      {/* Dangerous side: escape down + away to the dangerous side. */}
      <ArrowAt
        from={
          dangerSide === "R"
            ? { x: centre.x + R - 20, y: centre.y + R - 30 }
            : { x: centre.x - R + 20, y: centre.y + R - 30 }
        }
        to={
          dangerSide === "R"
            ? { x: centre.x + R + 30, y: centre.y + R + 10 }
            : { x: centre.x - R - 30, y: centre.y + R + 10 }
        }
        colour={DANGER}
      />
      {/* Navigable side: escape down + away too. */}
      <ArrowAt
        from={
          dangerSide === "R"
            ? { x: centre.x - R + 20, y: centre.y + R - 30 }
            : { x: centre.x + R - 20, y: centre.y + R - 30 }
        }
        to={
          dangerSide === "R"
            ? { x: centre.x - R - 30, y: centre.y + R + 10 }
            : { x: centre.x + R + 30, y: centre.y + R + 10 }
        }
        colour={NAV}
      />

      {/* Header + footer. */}
      <text
        x={10}
        y={20}
        fontSize={12}
        fontWeight={700}
        fill={STROKE}
      >
        {isN ? "NORTHERN" : "SOUTHERN"} HEMISPHERE TRS
      </text>
      <text
        x={VW - 10}
        y={VH - 8}
        fontSize={10}
        textAnchor="end"
        fill={MUTED}
      >
        Storm rotates {isN ? "anticlockwise" : "clockwise"}; tracks{" "}
        {isN ? "WNW then recurves NE" : "WSW then recurves SE"}.
      </text>
    </svg>
  );
}

function ArrowAround({
  centre,
  R,
  anticlockwise,
}: {
  centre: { x: number; y: number };
  R: number;
  anticlockwise: boolean;
}) {
  // Draw 4 curved arrows around the perimeter showing rotation.
  const arrows = [];
  for (let i = 0; i < 4; i++) {
    const angle = (i * 90 + 45) * (Math.PI / 180);
    const ax = centre.x + R * Math.cos(angle);
    const ay = centre.y + R * Math.sin(angle);
    // Tangent direction (90° from radial).
    const tangentAngle = angle + (anticlockwise ? -Math.PI / 2 : Math.PI / 2);
    const dx = Math.cos(tangentAngle) * 14;
    const dy = Math.sin(tangentAngle) * 14;
    const tipX = ax + dx;
    const tipY = ay + dy;
    arrows.push(
      <g key={i}>
        <line
          x1={ax - dx * 0.6}
          y1={ay - dy * 0.6}
          x2={tipX}
          y2={tipY}
          stroke={MUTED}
          strokeWidth={1.5}
        />
        <polygon
          points={`${tipX},${tipY} ${tipX - dx * 0.4 - dy * 0.25},${tipY - dy * 0.4 + dx * 0.25} ${tipX - dx * 0.4 + dy * 0.25},${tipY - dy * 0.4 - dx * 0.25}`}
          fill={MUTED}
        />
      </g>,
    );
  }
  return <g>{arrows}</g>;
}

function ArrowAt({
  from,
  to,
  colour,
}: {
  from: { x: number; y: number };
  to: { x: number; y: number };
  colour: string;
}) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  const ux = dx / len;
  const uy = dy / len;
  return (
    <g>
      <line
        x1={from.x}
        y1={from.y}
        x2={to.x}
        y2={to.y}
        stroke={colour}
        strokeWidth={2}
      />
      <polygon
        points={`${to.x},${to.y} ${to.x - 8 * ux - 4 * uy},${to.y - 8 * uy + 4 * ux} ${to.x - 8 * ux + 4 * uy},${to.y - 8 * uy - 4 * ux}`}
        fill={colour}
      />
    </g>
  );
}
